//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  SyndicationDestinationWithProductIds,
  SyndicationProduct,
} from '@apis/campaign-requests/sampling/campaigns/samplingCampaignsApiModel';
import { useGetSamplingProductPdpUrlQuery } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { SyndicationDestinationWithProducts } from '@campaigns/types/sampling/SamplingSyndicationTypes';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { isDefined } from '@utils/filterPredicates';

/**
 * Maps destinations with their associated products and calculates the total number of reviews.
 * Filters out destinations that are missing a name or have no associated products.
 *
 * @param destinations - An array of destinations with associated external product IDs.
 * @param products - An array of products.
 * @returns An array of destinations each containing their associated products and total number of reviews.
 */
export const mapDestinationsWithProducts = (
  destinations: SyndicationDestinationWithProductIds[] = [],
  products: SyndicationProduct[] = [],
): SyndicationDestinationWithProducts[] => {
  return destinations
    .filter(
      (
        destination,
      ): destination is SyndicationDestinationWithProductIds & {
        destination: string;
      } => !!destination.destination,
    ) // Filter out undefined destinations
    .map((destination) => {
      const externalProductIds = destination.externalProductIds ?? [];

      // Map associated products using the external product IDs
      const associatedProducts: SyndicationProduct[] = externalProductIds
        .map((id) =>
          products.find((product) => product.externalProductId === id),
        )
        .filter(isDefined);

      // Calculate total number of reviews for the associated products
      const numberReviews = associatedProducts.reduce(
        (acc, product) => acc + (product.numberReviews ?? 0),
        0,
      );

      return {
        destination: destination.destination,
        products: associatedProducts,
        numberReviews,
        destinationClientId: destination.destinationClientId,
      };
    });
};
